<template>
  <b-modal :id="id" size="xl" no-close-on-backdrop @shown="laden">
    <template #modal-header>
      <modal-close-button @confirmed="closeModal" />
      <h4 class="modal-title mx-auto">{{ $t("global.assignrooms") }}</h4>
    </template>

    <div class="row">
      <div v-if="lehrgang" class="col-xl-4">
        <div class="form-group">
          <v-select
            v-model="modul"
            label="bezeichnung"
            :options="module"
            :reduce="(m) => m.id"
            :clearable="false"
            :placeholder="$t('global.module')"
            @input="modulGewechselt"
          >
            <span slot="no-options">{{ $t("global.taptosearch") }}</span>
          </v-select>
          <label :class="{ 'text-danger': !modul }">{{ $t("global.module") }}</label>
        </div>
      </div>
      <div class="col-xl-4">
        <div class="form-group">
          <v-select
            v-model="zimmer"
            :getOptionLabel="kontingentLabel"
            :options="passendeKontingente"
            :reduce="(k) => k.id"
            :placeholder="$t('global.hotelopen')"
          >
            <span slot="no-options">{{ $t("global.taptosearch") }}</span>
          </v-select>
          <label>{{ $t("global.hotel") }}</label>
        </div>
      </div>
      <div class="col-xl-4 d-flex align-items-center mb-3">
        <b-form-checkbox v-model="nurMitZimmerwunsch" switch>
          {{ $t("global.onlywithroomrequest") }}
        </b-form-checkbox>
      </div>
    </div>

    <b-table
      small
      responsive
      show-empty
      sticky-header="420px"
      :items="angezeigteZeilen"
      :fields="fields"
      :busy="laedt"
    >
      <template v-slot:head(ausgewaehlt)>
        <b-form-checkbox :checked="alleGewaehlt" @change="alleWaehlen"></b-form-checkbox>
      </template>
      <template v-slot:cell(ausgewaehlt)="row">
        <b-form-checkbox v-model="row.item.ausgewaehlt" :disabled="row.item.terminIds.length === 0"></b-form-checkbox>
      </template>
      <template v-slot:cell(reservationstyp)="row">
        <v-select
          v-model="row.item.reservationstyp"
          label="bezeichnung"
          :options="reservationstypen"
          :reduce="(t) => t.id"
          :clearable="false"
          class="v-select-sm"
        ></v-select>
      </template>
      <template v-slot:cell(zusatzfrage)="row">
        <b-badge v-if="row.item.zusatzfrage" :variant="row.item.zusatzfrage === 'nein' ? 'secondary' : 'success'">
          {{ antwortText(row.item.zusatzfrage) }}
        </b-badge>
      </template>
      <template v-slot:cell(termine)="row">
        <span role="button" class="text-primary" @click="row.toggleDetails">
          {{ row.item.terminIds.length }} / {{ row.item.anzahlTermine }}
        </span>
        <span v-if="row.item.bereitsReserviert" class="ml-1 text-muted">
          ({{ row.item.bereitsReserviert }} {{ $t("global.alreadyreserved") }})
        </span>
      </template>
      <template v-slot:cell(voranreise)="row">
        <b-form-checkbox v-model="row.item.voranreise"></b-form-checkbox>
      </template>
      <template #row-details="row">
        <div class="mx-4 my-1">
          <b-form-checkbox-group v-model="row.item.terminIds" stacked>
            <b-form-checkbox
              v-for="termin in termine"
              :key="termin.id"
              :value="termin.id"
            >
              {{ termin.von }} – {{ termin.bis }} · {{ termin.veranstaltung ? termin.veranstaltung.bezeichnung : "" }}
            </b-form-checkbox>
          </b-form-checkbox-group>
        </div>
      </template>
      <template #table-busy>
        <div class="text-center text-primary my-2"><b-spinner class="align-middle"></b-spinner></div>
      </template>
      <template #empty>
        <div class="text-center text-primary my-2">
          <strong>{{ $t("global.nodataavailable") }}</strong>
        </div>
      </template>
    </b-table>

    <template #modal-footer>
      <span class="mr-auto text-muted">{{ anzahlGewaehlt }} {{ $t("global.selected") }}</span>
      <button class="btn btn-default mr-2" @click="closeModal">{{ $t("global.cancel") }}</button>
      <button class="btn btn-primary" :disabled="anzahlGewaehlt === 0 || saving" @click="zuteilen">
        {{ $t("global.assignrooms") }}
      </button>
    </template>
  </b-modal>
</template>

<script>
import AppApi from "@/api/AppApi";
import ModalCloseButton from "@/components/global/ModalCloseButton";
import { apiErrorToAlert } from "@/utils/Errorhandler";

/**
 * «Zimmer zuteilen»: Vorschläge aus Anmeldungen (Teilnehmende) und Dozenteneinsätzen (Dozierende),
 * alles an-/abwählbar inkl. Termine pro Person. Pro Person und zusammenhängendem Block entsteht eine Reservation;
 * bereits reservierte Termine werden übersprungen.
 * Öffnen über ref: this.$refs.<ref>.oeffnen()
 */
export default {
  name: "ZimmerZuteilenModal",
  components: { ModalCloseButton },
  props: {
    id: { type: String, default: "zimmer-zuteilen-modal" },
    veranstaltung: { type: String, required: false },
    lehrgang: { type: String, required: false },
    module: { type: Array, default: () => [] },
    hotelkontingente: { type: Array, default: () => [] },
    reservationstypen: { type: Array, default: () => [] },
  },
  data() {
    return {
      modul: null,
      zimmer: null,
      termine: [],
      zeilen: [],
      nurMitZimmerwunsch: false,
      laedt: false,
      saving: false,
    };
  },
  computed: {
    fields() {
      return [
        { key: "ausgewaehlt", label: "" },
        { key: "person.bezeichnung", label: this.$t("global.person"), sortable: true },
        { key: "reservationstyp", label: this.$t("global.reservationtype") },
        { key: "zusatzfrage", label: this.$t("global.roomrequest") },
        { key: "termine", label: this.$t("global.appointments") },
        { key: "voranreise", label: this.$t("global.arrivaltheeveningbefore") },
      ];
    },
    passendeKontingente() {
      if (!this.lehrgang) return this.hotelkontingente;
      return this.hotelkontingente.filter((k) => !k.veranstaltung || k.veranstaltung.id === this.modul);
    },
    angezeigteZeilen() {
      if (!this.nurMitZimmerwunsch) return this.zeilen;
      return this.zeilen.filter((z) => z.zusatzfrage === "ja" || z.zusatzfrage === "ja_vorabend");
    },
    alleGewaehlt() {
      const waehlbar = this.angezeigteZeilen.filter((z) => z.terminIds.length > 0);
      return waehlbar.length > 0 && waehlbar.every((z) => z.ausgewaehlt);
    },
    anzahlGewaehlt() {
      return this.zeilen.filter((z) => z.ausgewaehlt && z.terminIds.length > 0).length;
    },
  },
  methods: {
    oeffnen() {
      this.modul = this.lehrgang && this.module.length > 0 ? this.module[0].id : null;
      this.zimmer = null;
      this.nurMitZimmerwunsch = false;
      this.$bvModal.show(this.id);
    },
    kontingentLabel(kontingent) {
      const modul = this.lehrgang
        ? ` (${kontingent.veranstaltung ? kontingent.veranstaltung.bezeichnung : this.$t("global.allmodules")})`
        : "";
      return `${kontingent.gebaeude?.bezeichnung || ""}${modul}`;
    },
    antwortText(antwort) {
      if (antwort === "ja_vorabend") return this.$t("global.arrivaltheeveningbefore");
      return antwort === "ja" ? this.$t("global.yes") : this.$t("global.no");
    },
    modulGewechselt() {
      if (this.zimmer && !this.passendeKontingente.some((k) => k.id === this.zimmer)) this.zimmer = null;
      this.laden();
    },
    laden() {
      const params = this.lehrgang ? { modul: this.modul } : { veranstaltung: this.veranstaltung };
      if (!params.modul && !params.veranstaltung) return;
      // genau ein passendes Hotel → vorauswählen
      if (!this.zimmer && this.passendeKontingente.length === 1) this.zimmer = this.passendeKontingente[0].id;

      this.laedt = true;
      AppApi.get("zimmer-zuteilung/", { params })
        .then((response) => {
          this.termine = response.data.termine || [];
          this.zeilen = (response.data.vorschlaege || []).map((v) => ({
            person: v.person,
            reservationstyp: v.reservationstyp?.id || null,
            zusatzfrage: v.zusatzfrage,
            terminIds: [...v.terminIds],
            anzahlTermine: v.anzahlTermine,
            bereitsReserviert: v.bereitsReserviert,
            voranreise: v.voranreise,
            // Dozierende immer vorauswählen, Teilnehmende nur ohne Zusatzfrage oder mit Zimmerwunsch
            ausgewaehlt:
              v.terminIds.length > 0 &&
              (v.quelle === "dozent" || !v.zusatzfrage || v.zusatzfrage !== "nein"),
          }));
        })
        .catch((e) => this.$notify(apiErrorToAlert(e)))
        .finally(() => {
          this.laedt = false;
        });
    },
    alleWaehlen(checked) {
      this.angezeigteZeilen.forEach((z) => {
        if (z.terminIds.length > 0) z.ausgewaehlt = checked;
      });
    },
    zuteilen() {
      const eintraege = this.zeilen
        .filter((z) => z.ausgewaehlt && z.terminIds.length > 0)
        .map((z) => ({
          person: z.person.id,
          reservationstyp: z.reservationstyp,
          termine: z.terminIds,
          voranreise: z.voranreise,
        }));
      if (eintraege.some((e) => !e.reservationstyp)) {
        this.$notify({ type: "warn", title: this.$t("global.actionfailed"), text: this.$t("global.reservationtype") });
        return;
      }

      this.saving = true;
      AppApi.post("zimmer-zuteilung/", { zimmer: this.zimmer || null, eintraege })
        .then((response) => {
          this.$notify({
            type: "success",
            title: this.$t("global.actionsuccessful"),
            text: `${response.data.erstellteReservationen} ${this.$t("global.roomreservations")}`,
          });
          this.$emit("saved");
          this.closeModal();
        })
        .catch((e) => this.$notify(apiErrorToAlert(e)))
        .finally(() => {
          this.saving = false;
        });
    },
    closeModal() {
      this.$bvModal.hide(this.id);
    },
  },
};
</script>
